import { ScimEntityService } from '../../services/ScimEntityService';
import { getJobCodeByRefId } from '../../utils/jobCodeLookup';
import { debugVerboseLog } from '../../utils/debugLog';

/**
 * Result of parsing a legacy filter
 */
export interface ParsedLegacyFilter {
    entityType: string;
    filter: string;
    error?: string;
    untranslatedJobCodeGuids?: string[];
}

/**
 * Parse legacy filter syntax and convert to SCIM filter
 * @param legacyFilterText - The legacy filter string (e.g., "/Person[filter]")
 * @returns Parsed filter with entityType and filter string, or null if parsing failed
 */
export function parseLegacyFilter(legacyFilterText: string): ParsedLegacyFilter | null {
    const untranslatedJobCodeGuids: string[] = [];

    try {
        if (!legacyFilterText || !legacyFilterText.trim()) {
            return { entityType: '', filter: '', error: 'Legacy filter cannot be empty' };
        }

        const trimmed = legacyFilterText.trim();

        if (!trimmed.startsWith('/')) {
            return { entityType: '', filter: '', error: 'Legacy filter must start with /' };
        }

        const firstSlashIndex = 0;
        let entityTypeEnd = trimmed.indexOf('[', firstSlashIndex + 1);

        if (entityTypeEnd === -1) {
            const nextSlash = trimmed.indexOf('/', firstSlashIndex + 1);
            if (nextSlash !== -1) {
                entityTypeEnd = nextSlash;
            } else {
                return { entityType: '', filter: '', error: 'Legacy filter must contain [ after entity type' };
            }
        }

        let entityType = trimmed.substring(firstSlashIndex + 1, entityTypeEnd).trim();

        if (!entityType) {
            return { entityType: '', filter: '', error: 'Entity type cannot be empty' };
        }

        if (entityType === 'ResponsibilityCenter') {
            entityType = 'Rc';
        }

        let bracketCount = 0;
        let filterStart = entityTypeEnd;
        let filterEnd = -1;
        let inString = false;
        let stringChar = '';

        for (let i = filterStart; i < trimmed.length; i++) {
            const char = trimmed[i];

            if (char === '[' && !inString) {
                if (bracketCount === 0) {
                    filterStart = i + 1;
                }
                bracketCount++;
            } else if (char === ']' && !inString) {
                bracketCount--;
                if (bracketCount === 0) {
                    filterEnd = i;
                    break;
                }
            } else if ((char === '"' || char === "'") && (i === 0 || trimmed[i - 1] !== '\\')) {
                if (!inString) {
                    inString = true;
                    stringChar = char;
                } else if (char === stringChar) {
                    inString = false;
                    stringChar = '';
                }
            }
        }

        if (filterEnd === -1) {
            return { entityType: '', filter: '', error: 'Unmatched brackets in legacy filter' };
        }

        const filterContent = trimmed.substring(filterStart, filterEnd).trim();

        if (!filterContent) {
            return { entityType: '', filter: '', error: 'Filter content cannot be empty' };
        }

        let scimFilter = filterContent;

        scimFilter = scimFilter.replace(/\bResponsibilityCenterMemberOf\b/gi, 'Rcs');
        scimFilter = scimFilter.replace(/\bResponsibilityCenter\b/gi, 'Rc');
        scimFilter = scimFilter.replace(/\bdcpsUnionMembership\b/gi, 'Union');
        scimFilter = scimFilter.replace(/\bdcpsType\b/gi, 'EntityType');
        // not(starts-with(dcpsID, '40')) -> not(EntityType eq "Applicant") (before dcpsID -> Id replacement)
        scimFilter = scimFilter.replace(/not\s*\(\s*starts-with\s*\(\s*dcpsID\s*,\s*['"]40['"]\s*\)\s*\)/gi, 'not(EntityType eq "Applicant")');
        scimFilter = scimFilter.replace(/\bdcpsID\b/gi, 'Id');
        scimFilter = scimFilter.replace(/\bJobDes\b/gi, 'JobTitle');
        // JobCodeMemberOf eq "GUID" -> RcJobCode ew "JobCode" (lookup GUID to JobCode)
        // Pattern matches: JobCodeMemberOf = 'guid' or JobCodeMemberOf eq "guid" (with optional parentheses)
        scimFilter = scimFilter.replace(/\bJobCodeMemberOf\b\s+(?:eq|=)\s+["']([^"']+)["']/gi, (_match, guid) => {
            const trimmedGuid = guid.trim();
            const jobCode = getJobCodeByRefId(trimmedGuid);
            if (jobCode) {
                // Prepend . to the JobCode value
                const jobCodeWithDot = `.${jobCode}`;
                return `RcJobCode ew "${jobCodeWithDot}"`;
            }
            // If lookup fails, capture the GUID and still translate the attribute name
            console.warn(`⚠️ JobCodeMemberOf lookup failed for GUID: ${trimmedGuid}`);
            untranslatedJobCodeGuids.push(trimmedGuid);
            return `RcJobCode eq "${trimmedGuid}"`;
        });
        // OneViewPersonaName: student -> EntityType Student (supports '=' or 'eq', do before mapping to AcademicRole)
        scimFilter = scimFilter.replace(/\bOneViewPersonaName\b\s+(?:eq|=)\s*["']student["']/gi, 'EntityType eq "Student"');
        scimFilter = scimFilter.replace(/\bOneViewPersonaName\b/gi, 'AcademicRole');
        scimFilter = scimFilter.replace(/['"]824418f5-d876-45e5-99a1-d88c1d6b60ca['"]/gi, '"300061"');
        scimFilter = scimFilter.replace(/not\s*\(\s*starts-with\s*\(\s*ResponsibilityCenter\s*,\s*['"]SCH-['"]\s*\)\s*\)/gi, 'isCharter eq false');
        // dcpsRole conversions (specific cases first)
        scimFilter = scimFilter.replace(/\bdcpsRole\b\s+eq\s+["']charter["']/gi, 'IsCharter eq true');
        // dcpsRole = 'Principal' -> Role eq "Principal" (supports both '=' and 'eq', both quote types)
        scimFilter = scimFilter.replace(/\bdcpsRole\b\s+(?:eq|=)\s+["']principal["']/gi, 'Role eq "Principal"');
        // Remove mediaspecialist role clauses entirely by replacing with a neutral true
        scimFilter = scimFilter.replace(/\bdcpsRole\b\s+eq\s+["']mediaspecialist["']/gi, 'true');
        // General dcpsRole -> Role translation (for any remaining dcpsRole references)
        scimFilter = scimFilter.replace(/\bdcpsRole\b/gi, 'Role');
        // dcpsSAPUser = 'X' -> SapUser eq true (supports both '=' and 'eq', both quote types)
        scimFilter = scimFilter.replace(/\bdcpsSAPUser\b\s+(?:eq|=)\s+["']x["']/gi, 'SapUser eq true');
        // IsPeopleManager eq "true" -> Manages $count.gt 0 (supports both '=' and 'eq', both quote types)
        scimFilter = scimFilter.replace(/\bIsPeopleManager\b\s+(?:eq|=)\s+["']true["']/gi, 'Manages $count.gt 0');
        // OfficeLocation = 'x' -> Building ew 'x' (supports both '=' and 'eq', both quote types)
        scimFilter = scimFilter.replace(/\bOfficeLocation\b\s+(?:eq|=)\s+["']([^"']+)["']/gi, 'Building ew "$1"');
        scimFilter = scimFilter.replace(/starts-with\s*\(\s*([^,]+)\s*,\s*['"]([^'"]+)['"]\s*\)/gi, (_match, attribute, value) => {
            return `${attribute.trim()} sw "${value}"`;
        });
        // Rc sw "sch-" -> IsCharter eq true (case-insensitive, supports both single and double quotes)
        // This must come after starts-with conversion so it catches both direct sw patterns and converted starts-with patterns
        scimFilter = scimFilter.replace(/\bRc\b\s+sw\s+["']sch-["']/gi, 'IsCharter eq true');
        // Convert sw "% value %" patterns to co "value" (starts with %...% means contains the middle part)
        scimFilter = scimFilter.replace(/\b(\w+)\s+sw\s+["']%\s*([^%]+?)\s*%["']/gi, (_match, attribute, value) => {
            return `${attribute} co "${value.trim()}"`;
        });
        scimFilter = scimFilter.replace(/ends-with\s*\(\s*([^,]+)\s*,\s*['"]([^'"]+)['"]\s*\)/gi, (_match, attribute, value) => {
            return `${attribute.trim()} ew "${value}"`;
        });
        scimFilter = scimFilter.replace(/contains\s*\(\s*([^,]+)\s*,\s*['"]([^'"]+)['"]\s*\)/gi, (_match, attribute, value) => {
            return `${attribute.trim()} co "${value}"`;
        });
        scimFilter = scimFilter.replace(/\s*=\s*/g, ' eq ');
        scimFilter = scimFilter.replace(/'/g, '"');
        // dcpsRole "Charter" => IsCharter true
        scimFilter = scimFilter.replace(/\bdcpsRole\b\s+eq\s+["']charter["']/gi, 'IsCharter eq true');

        return {
            entityType: entityType,
            filter: scimFilter,
            untranslatedJobCodeGuids: untranslatedJobCodeGuids.length > 0 ? untranslatedJobCodeGuids : undefined
        };
    } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to parse legacy filter';
        return { entityType: '', filter: '', error: errorMsg };
    }
}

/**
 * Generic function to optimize multiple attribute eq statements combined with 'or' into attribute $in array
 */
function optimizeAttributeEqStatements(filter: string, attributeName: string): string {
    let optimizedFilter = filter;

    // Pattern 1: Match sequences of (AttributeName eq "value") or (AttributeName eq "value") or ... (AttributeName eq "value")
    // Also handles (AttributeName = "value") since = gets converted to eq later
    const findAttributeEqSequence = (text: string): { start: number; end: number; values: string[] } | null => {
        // Escape special regex characters in attribute name
        const escapedAttr = attributeName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Match both 'eq' and '=' operators (case-insensitive)
        const pattern = new RegExp(`\\(${escapedAttr}\\s+(?:eq|=)\\s+["']([^"']+)["']\\)`, 'gi');

        // Find all matches first
        const allMatches: Array<{ index: number; length: number; value: string }> = [];
        let match;
        while ((match = pattern.exec(text)) !== null) {
            allMatches.push({
                index: match.index,
                length: match[0].length,
                value: match[1]
            });
        }

        if (allMatches.length < 2) return null;

        // Find the longest consecutive sequence connected by 'or'
        let bestSequence: { start: number; end: number; values: string[] } | null = null;
        let currentSequence: { start: number; end: number; values: string[] } | null = null;

        for (let i = 0; i < allMatches.length; i++) {
            const current = allMatches[i];

            if (currentSequence) {
                const prevEnd = currentSequence.end;
                const betweenText = text.substring(prevEnd, current.index).trim();

                // Check if connected by 'or' (case-insensitive, allowing for whitespace and parentheses)
                if (/^\)?\s*or\s*\(?\s*$/i.test(betweenText)) {
                    // Connected, extend sequence
                    currentSequence.end = current.index + current.length;
                    currentSequence.values.push(current.value);
                    continue;
                }

                // Not connected, save current sequence if it's long enough
                if (currentSequence.values.length >= 2) {
                    if (!bestSequence || currentSequence.values.length > bestSequence.values.length) {
                        bestSequence = currentSequence;
                    }
                }
            }

            // Start new sequence
            currentSequence = {
                start: current.index,
                end: current.index + current.length,
                values: [current.value]
            };
        }

        // Check final sequence
        if (currentSequence && currentSequence.values.length >= 2) {
            if (!bestSequence || currentSequence.values.length > bestSequence.values.length) {
                bestSequence = currentSequence;
            }
        }

        return bestSequence;
    };

    let changed = true;
    while (changed) {
        changed = false;
        const sequence = findAttributeEqSequence(optimizedFilter);

        if (sequence && sequence.values.length >= 2) {
            const valueArray = sequence.values.map(v => `"${v}"`).join(', ');
            const replacement = `${attributeName} $in [${valueArray}]`;
            optimizedFilter = optimizedFilter.substring(0, sequence.start) +
                replacement +
                optimizedFilter.substring(sequence.end);
            changed = true;
        }
    }

    // Pattern 2: Match sequences without parentheses: AttributeName eq "value" or AttributeName eq "value" or ...
    // Also handles AttributeName = "value" since = gets converted to eq later
    const findAttributeEqSequenceNoParens = (text: string): { start: number; end: number; values: string[] } | null => {
        // Escape special regex characters in attribute name
        const escapedAttr = attributeName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Match both 'eq' and '=' operators (case-insensitive)
        const pattern = new RegExp(`${escapedAttr}\\s+(?:eq|=)\\s+["']([^"']+)["']`, 'gi');

        const allMatches: Array<{ index: number; length: number; value: string }> = [];
        let match;
        while ((match = pattern.exec(text)) !== null) {
            allMatches.push({
                index: match.index,
                length: match[0].length,
                value: match[1]
            });
        }

        if (allMatches.length < 2) return null;

        let bestSequence: { start: number; end: number; values: string[] } | null = null;
        let currentSequence: { start: number; end: number; values: string[] } | null = null;

        for (let i = 0; i < allMatches.length; i++) {
            const current = allMatches[i];

            if (currentSequence) {
                const prevEnd = currentSequence.end;
                const betweenText = text.substring(prevEnd, current.index).trim();

                if (/^\s+or\s+$/i.test(betweenText)) {
                    currentSequence.end = current.index + current.length;
                    currentSequence.values.push(current.value);
                    continue;
                }

                if (currentSequence.values.length >= 2) {
                    if (!bestSequence || currentSequence.values.length > bestSequence.values.length) {
                        bestSequence = currentSequence;
                    }
                }
            }

            currentSequence = {
                start: current.index,
                end: current.index + current.length,
                values: [current.value]
            };
        }

        if (currentSequence && currentSequence.values.length >= 2) {
            if (!bestSequence || currentSequence.values.length > bestSequence.values.length) {
                bestSequence = currentSequence;
            }
        }

        return bestSequence;
    };

    changed = true;
    while (changed) {
        changed = false;
        const sequence = findAttributeEqSequenceNoParens(optimizedFilter);

        if (sequence && sequence.values.length >= 2) {
            const valueArray = sequence.values.map(v => `"${v}"`).join(', ');
            const replacement = `${attributeName} $in [${valueArray}]`;
            optimizedFilter = optimizedFilter.substring(0, sequence.start) +
                replacement +
                optimizedFilter.substring(sequence.end);
            changed = true;
        }
    }

    return optimizedFilter;
}

/**
 * Optimize multiple Rc eq statements combined with 'or' into Rc $in array
 */
function optimizeRcEqStatements(filter: string): string {
    let optimizedFilter = filter;

    // Pattern 1: Match sequences of (Rc eq "value") or (Rc eq "value") or ... (Rc eq "value")
    const findRcEqSequence = (text: string): { start: number; end: number; values: string[] } | null => {
        const pattern = /\(Rc\s+eq\s+"([^"]+)"\)/gi;

        // Find all matches first
        const allMatches: Array<{ index: number; length: number; value: string }> = [];
        let match;
        while ((match = pattern.exec(text)) !== null) {
            allMatches.push({
                index: match.index,
                length: match[0].length,
                value: match[1]
            });
        }

        if (allMatches.length < 2) return null;

        // Find the longest consecutive sequence connected by 'or'
        let bestSequence: { start: number; end: number; values: string[] } | null = null;
        let currentSequence: { start: number; end: number; values: string[] } | null = null;

        for (let i = 0; i < allMatches.length; i++) {
            const current = allMatches[i];

            if (currentSequence) {
                const prevEnd = currentSequence.end;
                const betweenText = text.substring(prevEnd, current.index).trim();

                // Check if connected by 'or' (case-insensitive, allowing for whitespace and parentheses)
                if (/^\)?\s*or\s*\(?\s*$/i.test(betweenText)) {
                    // Connected, extend sequence
                    currentSequence.end = current.index + current.length;
                    currentSequence.values.push(current.value);
                    continue;
                }

                // Not connected, save current sequence if it's long enough
                if (currentSequence.values.length >= 2) {
                    if (!bestSequence || currentSequence.values.length > bestSequence.values.length) {
                        bestSequence = currentSequence;
                    }
                }
            }

            // Start new sequence
            currentSequence = {
                start: current.index,
                end: current.index + current.length,
                values: [current.value]
            };
        }

        // Check final sequence
        if (currentSequence && currentSequence.values.length >= 2) {
            if (!bestSequence || currentSequence.values.length > bestSequence.values.length) {
                bestSequence = currentSequence;
            }
        }

        return bestSequence;
    };

    let changed = true;
    while (changed) {
        changed = false;
        const sequence = findRcEqSequence(optimizedFilter);

        if (sequence && sequence.values.length >= 2) {
            const valueArray = sequence.values.map(v => `"${v}"`).join(', ');
            const replacement = `Rc $in [${valueArray}]`;
            optimizedFilter = optimizedFilter.substring(0, sequence.start) +
                replacement +
                optimizedFilter.substring(sequence.end);
            changed = true;
        }
    }

    // Pattern 2: Match sequences without parentheses: Rc eq "value" or Rc eq "value" or ...
    const findRcEqSequenceNoParens = (text: string): { start: number; end: number; values: string[] } | null => {
        const pattern = /Rc\s+eq\s+"([^"]+)"/gi;

        const allMatches: Array<{ index: number; length: number; value: string }> = [];
        let match;
        while ((match = pattern.exec(text)) !== null) {
            allMatches.push({
                index: match.index,
                length: match[0].length,
                value: match[1]
            });
        }

        if (allMatches.length < 2) return null;

        let bestSequence: { start: number; end: number; values: string[] } | null = null;
        let currentSequence: { start: number; end: number; values: string[] } | null = null;

        for (let i = 0; i < allMatches.length; i++) {
            const current = allMatches[i];

            if (currentSequence) {
                const prevEnd = currentSequence.end;
                const betweenText = text.substring(prevEnd, current.index).trim();

                if (/^\s+or\s+$/i.test(betweenText)) {
                    currentSequence.end = current.index + current.length;
                    currentSequence.values.push(current.value);
                    continue;
                }

                if (currentSequence.values.length >= 2) {
                    if (!bestSequence || currentSequence.values.length > bestSequence.values.length) {
                        bestSequence = currentSequence;
                    }
                }
            }

            currentSequence = {
                start: current.index,
                end: current.index + current.length,
                values: [current.value]
            };
        }

        if (currentSequence && currentSequence.values.length >= 2) {
            if (!bestSequence || currentSequence.values.length > bestSequence.values.length) {
                bestSequence = currentSequence;
            }
        }

        return bestSequence;
    };

    changed = true;
    while (changed) {
        changed = false;
        const sequence = findRcEqSequenceNoParens(optimizedFilter);

        if (sequence && sequence.values.length >= 2) {
            const valueArray = sequence.values.map(v => `"${v}"`).join(', ');
            const replacement = `Rc $in [${valueArray}]`;
            optimizedFilter = optimizedFilter.substring(0, sequence.start) +
                replacement +
                optimizedFilter.substring(sequence.end);
            changed = true;
        }
    }

    return optimizedFilter;
}

/**
 * Optimize JobCode eq statements
 */
function optimizeJobCodeEqStatements(filter: string): string {
    return optimizeAttributeEqStatements(filter, 'JobCode');
}

/**
 * Optimize GradeLevel eq statements
 */
function optimizeGradeLevelEqStatements(filter: string): string {
    return optimizeAttributeEqStatements(filter, 'GradeLevel');
}

/**
 * Optimize DisplayName eq statements
 */
function optimizeDisplayNameEqStatements(filter: string): string {
    return optimizeAttributeEqStatements(filter, 'DisplayName');
}

/**
 * Optimize AccountName eq statements
 */
function optimizeAccountNameEqStatements(filter: string): string {
    return optimizeAttributeEqStatements(filter, 'AccountName');
}

/**
 * Apply all filter optimizations in the correct order
 */
function applyAllFilterOptimizations(filter: string): string {
    let optimized = filter;
    optimized = optimizeRcEqStatements(optimized);
    optimized = optimizeJobCodeEqStatements(optimized);
    optimized = optimizeGradeLevelEqStatements(optimized);
    optimized = optimizeDisplayNameEqStatements(optimized);
    optimized = optimizeAccountNameEqStatements(optimized);
    return optimized;
}

/**
 * Dependencies for RC resolution functions
 */
export interface RCResolutionDependencies {
    entityService: ScimEntityService;
    getLegacyIdToRcIdMap: () => Map<string, string>;
    addRC: (rcId: string, ids: string[]) => void;
    localRCCacheRef: { current: Map<string, string[]> };
    responsibilityCenterCache: Map<string, string[]>;
    rcIdResolutionPromisesRef: { current: Map<string, Promise<string | null>> };
    rcNotFoundCacheRef: { current: Set<string> };
    rcCacheLoaded: boolean;
    loadingRCCache: boolean;
}

/**
 * Resolve a single legacy RC ID to an RC Id
 */
async function resolveLegacyRcId(
    legacyId: string,
    deps: RCResolutionDependencies
): Promise<string | null> {
    const { getLegacyIdToRcIdMap, addRC, entityService, localRCCacheRef, rcIdResolutionPromisesRef } = deps;

    // First check if it's already an RC Id (exists as a key in the cache)
    const reverseMap = getLegacyIdToRcIdMap();
    const cachedId = reverseMap.get(legacyId);
    if (cachedId) {
        debugVerboseLog(`✅ resolveLegacyRcId: "${legacyId}" already in cache as RC Id "${cachedId}"`);
        return cachedId;
    }

    // Check if there's already an in-flight request for this legacy ID
    const existingPromise = rcIdResolutionPromisesRef.current.get(legacyId);
    if (existingPromise) {
        debugVerboseLog(`🔍 resolveLegacyRcId: Reusing in-flight request for "${legacyId}"`);
        return existingPromise;
    }

    // Create a new promise for this resolution
    const resolutionPromise = (async (): Promise<string | null> => {
        try {
            debugVerboseLog(`🌐 resolveLegacyRcId: Making API call for "${legacyId}"`);
            const response = await entityService.getEntitiesByType(
                'ResponsibilityCenter',
                1,
                25,
                undefined,
                `Ids co "${legacyId}"`,
                ['Id', 'DisplayName', 'Type', 'EntityType', 'Address', 'Ids']
            );

            const rcs = response.data || [];
            if (rcs.length > 0) {
                const rcId = rcs[0].Id || rcs[0].id;
                if (rcId) {
                    const ids = rcs[0].Ids || rcs[0].ids || [];
                    const idsArray = Array.isArray(ids) ? ids : [];
                    debugVerboseLog(`✅ resolveLegacyRcId: Resolved "${legacyId}" -> "${rcId}" with ${idsArray.length} legacy IDs`);
                    // Update local cache immediately (before Redux state update)
                    localRCCacheRef.current.set(rcId, idsArray);
                    addRC(rcId, idsArray);
                    // Remove from in-flight cache after completion
                    rcIdResolutionPromisesRef.current.delete(legacyId);
                    return rcId;
                }
            }
        } catch (err) {
            console.warn(`Failed to resolve legacy RC ID ${legacyId}:`, err);
        } finally {
            // Remove from in-flight cache after completion (success or failure)
            rcIdResolutionPromisesRef.current.delete(legacyId);
        }

        return null;
    })();

    // Store the promise in the in-flight cache
    rcIdResolutionPromisesRef.current.set(legacyId, resolutionPromise);
    return resolutionPromise;
}

/**
 * Batch resolve multiple legacy RC IDs in a single API call
 */
export async function batchResolveLegacyRcIds(
    legacyIds: string[],
    deps: RCResolutionDependencies
): Promise<Map<string, string>> {
    if (legacyIds.length === 0) {
        return new Map();
    }

    const {
        getLegacyIdToRcIdMap,
        addRC,
        entityService,
        localRCCacheRef,
        responsibilityCenterCache,
        rcCacheLoaded,
        loadingRCCache,
        rcIdResolutionPromisesRef
    } = deps;

    const reverseMap = getLegacyIdToRcIdMap();
    const results = new Map<string, string>();
    const idsToResolve: string[] = [];

    // Log cache state for debugging
    const localCacheSize = localRCCacheRef.current.size;
    const reduxCacheSize = responsibilityCenterCache.size;
    const reverseMapSize = reverseMap.size;

    const { rcNotFoundCacheRef } = deps;

    // Separate IDs that are already cached vs need resolution
    for (const legacyId of legacyIds) {
        const cachedId = reverseMap.get(legacyId);
        if (cachedId) {
            results.set(legacyId, cachedId);
        } else if (rcNotFoundCacheRef.current.has(legacyId)) {
            // ID was previously searched and not found - skip it
            debugVerboseLog(`⏭️ batchResolveLegacyRcIds: Skipping "${legacyId}" - previously not found`);
        } else {
            idsToResolve.push(legacyId);
        }
    }

    // If all IDs are cached, return immediately (no API call needed)
    if (idsToResolve.length === 0) {
        debugVerboseLog(`✅ batchResolveLegacyRcIds: All ${legacyIds.length} IDs already in cache, skipping API call`);
        return results;
    }

    console.warn(`🔍 batchResolveLegacyRcIds: Resolving ${idsToResolve.length} uncached IDs (${legacyIds.length - idsToResolve.length} already cached)`, {
        totalRequested: legacyIds.length,
        foundInCache: legacyIds.length - idsToResolve.length,
        missingFromCache: idsToResolve.length,
        missingIds: idsToResolve.slice(0, 10),
        localCacheSize,
        reduxCacheSize,
        reverseMapSize,
        rcCacheLoaded,
        loadingRCCache
    });

    // Batch resolve all uncached IDs in a single API call
    try {
        // Create filter: Ids $in ["id1", "id2", "id3", ...]
        const idList = idsToResolve.map(id => `"${id}"`).join(', ');
        const filter = `Ids $in [${idList}]`;

        const response = await entityService.getEntitiesByType(
            'ResponsibilityCenter',
            1,
            100, // Increased to handle multiple results
            undefined,
            filter,
            ['Id', 'DisplayName', 'Type', 'EntityType', 'Address', 'Ids']
        );

        const rcs = response.data || [];

        // Process results: for each RC, check which legacy IDs it contains
        for (const rc of rcs) {
            const rcId = rc.Id || rc.id;
            if (!rcId) continue;

            const ids = rc.Ids || rc.ids || [];
            const idsArray = Array.isArray(ids) ? ids : [];

            // Update local cache immediately (before Redux state update)
            localRCCacheRef.current.set(rcId, idsArray);
            // Add to Redux cache (async, but local cache is already updated)
            addRC(rcId, idsArray);

            // Map each legacy ID in this RC's Ids array to the RC Id
            for (const legacyId of idsArray) {
                if (idsToResolve.includes(legacyId)) {
                    results.set(legacyId, rcId);
                }
            }
        }

        // Cache IDs that were not found to prevent retrying them
        const notFoundIds = idsToResolve.filter(id => !results.has(id));
        for (const notFoundId of notFoundIds) {
            rcNotFoundCacheRef.current.add(notFoundId);
        }

        debugVerboseLog(`✅ batchResolveLegacyRcIds: Resolved ${results.size} IDs from API (${notFoundIds.length} not found and cached)`);
    } catch (err) {
        console.error('❌ Failed to batch resolve legacy RC IDs:', err);
        // Only fall back to individual resolution if we have a small number of IDs
        // For large batches, this would create too many API calls
        if (idsToResolve.length <= 5) {
            console.warn(`⚠️ Falling back to individual resolution for ${idsToResolve.length} IDs. This should be rare!`);
            // Fall back to individual resolution for any that failed
            // NOTE: This creates multiple API calls - should only happen on error with small batches
            for (const legacyId of idsToResolve) {
                if (!results.has(legacyId)) {
                    console.warn(`⚠️ batchResolveLegacyRcIds: Falling back to individual call for "${legacyId}"`);
                    const resolvedId = await resolveLegacyRcId(legacyId, deps);
                    if (resolvedId) {
                        results.set(legacyId, resolvedId);
                    }
                }
            }
        } else {
            console.error(`❌ Cannot fall back to individual resolution for ${idsToResolve.length} IDs - would create too many API calls. Please investigate the batch resolution error.`);
            // Don't make individual calls for large batches - log error instead
        }
    }

    return results;
}

/**
 * Resolve legacy RC IDs in a filter string
 */
async function resolveLegacyRcIdsInFilter(
    filter: string,
    deps: RCResolutionDependencies
): Promise<string> {
    // Support Rc, Rcs, and PrincipalOf attribute names (all map values to RC IDs)
    const rcPattern = /\b(Rc|Rcs|PrincipalOf)\s+(eq|ne|co|sw|ew|pr|gt|ge|lt|le)\s+["']([^"']+)["']/gi;

    const matches = Array.from(filter.matchAll(rcPattern));
    if (matches.length === 0) {
        return filter;
    }

    // Extract unique legacy IDs from matches
    const legacyIds = Array.from(new Set(matches.map(match => match[3])));

    // Check which IDs are already RC Ids (don't need resolution)
    const idsToResolve: string[] = [];
    const idToRcIdMap = new Map<string, string>(); // Maps legacy ID -> RC Id

    const { responsibilityCenterCache, localRCCacheRef, getLegacyIdToRcIdMap, rcNotFoundCacheRef } = deps;

    // Get the reverse map (legacy ID -> RC Id) from all loaded RCs
    const reverseMap = getLegacyIdToRcIdMap();

    for (const legacyId of legacyIds) {
        // First check the reverse map (legacy ID -> RC Id) - this should have all IDs from loaded RCs
        const cachedRcId = reverseMap.get(legacyId);
        if (cachedRcId) {
            idToRcIdMap.set(legacyId, cachedRcId);
        } else {
            // Not in reverse map - check if it's already an RC Id itself
            const isRcId = responsibilityCenterCache.has(legacyId) || localRCCacheRef.current.has(legacyId);
            if (isRcId) {
                idToRcIdMap.set(legacyId, legacyId);
            } else if (rcNotFoundCacheRef.current.has(legacyId)) {
                // ID was previously searched and not found - skip it and use original value
                debugVerboseLog(`⏭️ resolveLegacyRcIdsInFilter: Skipping "${legacyId}" - previously not found`);
                // Don't add to idToRcIdMap - will use original value as fallback
            } else {
                // Not found anywhere - needs resolution (but should be rare if we loaded all RCs)
                idsToResolve.push(legacyId);
            }
        }
    }

    // Batch resolve all legacy IDs that need resolution
    if (idsToResolve.length > 0) {
        const resolvedMap = await batchResolveLegacyRcIds(idsToResolve, deps);
        for (const [legacyId, rcId] of resolvedMap) {
            idToRcIdMap.set(legacyId, rcId);
        }
    }

    // Replace all matches in the filter with resolved RC Ids
    let resolvedFilter = filter;
    for (const match of matches) {
        const fullMatch = match[0];
        const attribute = match[1]; // 'Rc', 'Rcs', or 'PrincipalOf'
        const operator = match[2];
        const legacyId = match[3];
        const rcId = idToRcIdMap.get(legacyId) || legacyId; // Fallback to original if not resolved

        // Preserve the original attribute name (Rc, Rcs, or PrincipalOf) in the resolved filter
        const replacement = `${attribute} ${operator} "${rcId}"`;
        resolvedFilter = resolvedFilter.replace(fullMatch, replacement);
    }

    return resolvedFilter;
}

/**
 * Process a filter through the full conversion pipeline:
 * 1. Resolve legacy RC IDs
 * 2. Apply all optimizations
 */
export async function processFilterPipeline(
    filterString: string,
    deps: RCResolutionDependencies
): Promise<string> {
    // Step 1: Resolve legacy RC IDs
    let processedFilter = await resolveLegacyRcIdsInFilter(filterString, deps);

    // Step 2: Apply all optimizations
    processedFilter = applyAllFilterOptimizations(processedFilter);

    return processedFilter;
}


